import { Injectable } from '@angular/core';

/**
 * Small in-memory, session-only cache for downloaded video / 3D-model bytes.
 *
 * Re-opening the same document tab (e.g. switching between work-order document tabs) would
 * otherwise re-download and re-parse the same large FBX/GLB/MP4 file every time, since the
 * viewer components are destroyed/recreated by the `@if (selectedDocument ...)` template.
 * Caching the raw bytes here (keyed by document URL) makes re-opening a previously loaded file
 * instant. Bounded by both entry count and total bytes to avoid unbounded memory growth.
 */
@Injectable({ providedIn: 'root' })
export class DocumentViewerCacheService {
  private static readonly MAX_ENTRIES = 6;
  private static readonly MAX_TOTAL_BYTES = 250 * 1024 * 1024; // 250MB across all cached entries

  private readonly cache = new Map<string, ArrayBuffer | Blob>();
  private totalBytes = 0;

  get(url: string): ArrayBuffer | Blob | undefined {
    const entry = this.cache.get(url);
    if (entry) {
      // Refresh recency (Map preserves insertion order; re-inserting moves it to the end).
      this.cache.delete(url);
      this.cache.set(url, entry);
    }
    return entry;
  }

  set(url: string, data: ArrayBuffer | Blob): void {
    const size = data instanceof Blob ? data.size : data.byteLength;
    if (size > DocumentViewerCacheService.MAX_TOTAL_BYTES) {
      // Single file too large to bother caching.
      return;
    }
    this.evictIfNeeded(size);
    this.cache.set(url, data);
    this.totalBytes += size;
  }

  private evictIfNeeded(incomingSize: number): void {
    const iterator = this.cache.keys();
    while (
      (this.cache.size >= DocumentViewerCacheService.MAX_ENTRIES ||
        this.totalBytes + incomingSize > DocumentViewerCacheService.MAX_TOTAL_BYTES) &&
      this.cache.size > 0
    ) {
      const oldestKey = iterator.next().value as string | undefined;
      if (!oldestKey) break;
      const oldest = this.cache.get(oldestKey);
      const oldestSize = oldest instanceof Blob ? oldest.size : (oldest?.byteLength ?? 0);
      this.cache.delete(oldestKey);
      this.totalBytes -= oldestSize;
    }
  }
}
